import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { EquipmentOrdersPanel } from "../components/samourais/EquipmentOrdersPanel";
import { RenewalsPanel } from "../components/RenewalsPanel";
import { ProgramEquipmentEditor } from "../components/equipment/ProgramEquipmentEditor";
import { StudentEquipmentCheck } from "../components/equipment/StudentEquipmentCheck";
import { EquipmentCatalogAdmin } from "../components/equipment/EquipmentCatalogAdmin";
import { NextSupplierOrder } from "../components/equipment/NextSupplierOrder";
import { ProfitReport } from "../components/equipment/ProfitReport";
import { CombinedLinkPanel } from "../components/billing/CombinedLinkPanel";
import { SupplierProfiles } from "../components/equipment/SupplierProfiles";
import { EquipmentReport } from "../components/equipment/EquipmentReport";
import { InventoryPanel } from "../components/equipment/InventoryPanel";
import { OrderCalendarPanel } from "../components/equipment/OrderCalendarPanel";

const STATUS_META = {
  a_faire: { label: "À faire", cls: "bg-zinc-800 text-zinc-400 border-zinc-700" },
  en_cours: { label: "En cours", cls: "bg-amber-500/15 text-amber-400 border-amber-700" },
  complete_membre: { label: "Complété (membre)", cls: "bg-sky-500/15 text-sky-400 border-sky-700" },
  verifie: { label: "Vérifié coach/admin", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-700" },
};
const NEXT = { a_faire: "en_cours", en_cours: "complete_membre", complete_membre: "verifie", verifie: "a_faire" };
const FEE_META = {
  paye: { label: "Payé", cls: "text-emerald-400" },
  non_paye: { label: "Non payé", cls: "text-red-400" },
  non_applicable: { label: "Non applicable", cls: "text-zinc-500" },
};
const PRIO = { haute: "bg-red-500/20 text-red-400", moyenne: "bg-amber-500/20 text-amber-400", basse: "bg-zinc-700 text-zinc-400" };

export default function PremierMoisModule() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ discipline: "", statut: "" });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (filters.discipline) p.set("discipline", filters.discipline);
      if (filters.statut) p.set("statut", filters.statut);
      const { data } = await api.get(`/equipment/admin/overview?${p.toString()}`);
      setMembers(data.members || []);
    } catch { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="premier-mois-module">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <div>
          <h1 className="text-2xl font-bold text-zinc-100">📋 Premier mois — Obligations & équipement</h1>
          <p className="text-sm text-zinc-500">Frais d'inscription, équipement obligatoire par discipline et validation coach/admin.</p>
        </div>
        <button onClick={() => setSettingsOpen(true)} data-testid="fee-settings-btn" className="border border-zinc-700 text-zinc-200 px-4 py-2 rounded text-sm font-semibold hover:bg-zinc-800">⚙️ Frais d'inscription</button>
      </div>
      <EquipmentOrdersPanel programs={["pcv", "pcn", "master", "petits_samourais"]} />
      <RenewalsPanel />
      <details className="mb-6" data-testid="pe-editor-toggle">
        <summary className="cursor-pointer text-sm font-semibold text-zinc-200 rounded-xl border border-zinc-800 bg-zinc-900/60 px-4 py-3">🥋 Équipement par programme — éditeur (Obligatoire → Recommandé → Avancé / compétition)</summary>
        <div className="mt-2"><ProgramEquipmentEditor /></div>
      </details>
      <details className="mb-6" data-testid="cat-toggle">
        <summary className="cursor-pointer text-sm font-semibold text-zinc-200 rounded-xl border border-zinc-800 bg-zinc-900/60 px-4 py-3">🛒 Catalogue équipement & uniforme — fournisseur, coût, variantes, statut (prix manuels, aucun Stripe)</summary>
        <div className="mt-2"><EquipmentCatalogAdmin /></div>
      </details>
      <details className="mb-6" data-testid="inv-toggle">
        <summary className="cursor-pointer text-sm font-semibold text-zinc-200 rounded-xl border border-zinc-800 bg-zinc-900/60 px-4 py-3">📦 Inventaire complet — stock par taille/couleur, réservé, prêté, défectueux, alertes de réappro</summary>
        <div className="mt-2"><InventoryPanel /></div>
      </details>
      <details className="mb-6" data-testid="cal-toggle">
        <summary className="cursor-pointer text-sm font-semibold text-zinc-200 rounded-xl border border-zinc-800 bg-zinc-900/60 px-4 py-3">📅 Calendrier des commandes — Rentrée · Hiver · Printemps · Été (ouverture, limite de paiement)</summary>
        <div className="mt-2"><OrderCalendarPanel /></div>
      </details>
      <details className="mb-6" data-testid="nso-toggle">
        <summary className="cursor-pointer text-sm font-semibold text-zinc-200 rounded-xl border border-zinc-800 bg-zinc-900/60 px-4 py-3">📦 Prochaine commande fournisseur · précommandes · tailles à confirmer</summary>
        <div className="mt-2"><NextSupplierOrder /></div>
      </details>
      <details className="mb-6" data-testid="profit-toggle">
        <summary className="cursor-pointer text-sm font-semibold text-zinc-200 rounded-xl border border-zinc-800 bg-zinc-900/60 px-4 py-3">📈 Profit équipement · marge par année et par produit · coûts fournisseur</summary>
        <div className="mt-2"><ProfitReport /></div>
      </details>
      <details className="mb-6" data-testid="cl-toggle">
        <summary className="cursor-pointer text-sm font-semibold text-zinc-200 rounded-xl border border-zinc-800 bg-zinc-900/60 px-4 py-3">🔗 Fusionner des liens de paiement · un seul paiement pour plusieurs montants</summary>
        <div className="mt-2"><CombinedLinkPanel /></div>
      </details>
      <details className="mb-6" data-testid="sp-toggle">
        <summary className="cursor-pointer text-sm font-semibold text-zinc-200 rounded-xl border border-zinc-800 bg-zinc-900/60 px-4 py-3">🏭 Fiches fournisseurs — prix par volume, délais, conditions, historique</summary>
        <div className="mt-2"><SupplierProfiles /></div>
      </details>
      <details className="mb-6" data-testid="rep-toggle">
        <summary className="cursor-pointer text-sm font-semibold text-zinc-200 rounded-xl border border-zinc-800 bg-zinc-900/60 px-4 py-3">📊 Rapport équipement — gants, protège-tibias, shorts, gi par taille</summary>
        <div className="mt-2"><EquipmentReport /></div>
      </details>

      <div className="flex flex-wrap gap-2 my-4">
        <select value={filters.discipline} onChange={(e) => setFilters((f) => ({ ...f, discipline: e.target.value }))} data-testid="filter-discipline" className="bg-zinc-900 border border-zinc-800 rounded px-3 py-1.5 text-sm text-zinc-200">
          <option value="">Toutes disciplines</option>
          <option value="muay_thai_kickboxing">Kickboxing / Muay Thaï</option>
          <option value="jujutsu_japonais">Jiu-jitsu Can-Ryu</option>
          <option value="bjj">Jiu-jitsu brésilien</option>
        </select>
        <select value={filters.statut} onChange={(e) => setFilters((f) => ({ ...f, statut: e.target.value }))} data-testid="filter-statut" className="bg-zinc-900 border border-zinc-800 rounded px-3 py-1.5 text-sm text-zinc-200">
          <option value="">Tous statuts</option>
          <option value="incomplet">Incomplets</option>
          <option value="complet">Complets</option>
          <option value="retard">En retard (+30 j)</option>
        </select>
      </div>

      {loading && <div className="text-center py-16 text-zinc-500">Chargement…</div>}
      {!loading && (
        <div className="border border-zinc-800 rounded-lg overflow-hidden" data-testid="members-table">
          <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-zinc-900 text-[11px] uppercase tracking-wider text-zinc-500">
            <div className="col-span-3">Membre</div>
            <div className="col-span-3">Discipline</div>
            <div className="col-span-2">Frais</div>
            <div className="col-span-2">Progression</div>
            <div className="col-span-2 text-right">Statut</div>
          </div>
          {members.length === 0 && <div className="px-4 py-8 text-center text-zinc-500 text-sm">Aucun membre.</div>}
          {members.map((m) => (
            <button key={m.contract_id} onClick={() => setDetailId(m.contract_id)} data-testid={`member-row-${m.contract_id}`}
              className="w-full grid grid-cols-12 gap-2 px-4 py-3 text-left text-sm border-t border-zinc-900 hover:bg-zinc-900/60 items-center">
              <div className="col-span-3">
                <div className="text-zinc-100">{m.full_name}</div>
                <div className="text-[11px] text-zinc-500">{m.belt || "—"} {m.overdue && <span className="text-red-400">· retard</span>}</div>
              </div>
              <div className="col-span-3 text-zinc-400 text-xs">{m.discipline_label}</div>
              <div className={`col-span-2 text-xs ${FEE_META[m.fee_status]?.cls}`}>{FEE_META[m.fee_status]?.label}{m.fee_status === "non_paye" && m.fee_amount ? ` (${m.fee_amount}$)` : ""}{m.fee_stage?.startsWith("renouvellement") ? <span className="block text-[10px] text-amber-300" data-testid={`fee-renewal-${m.id || m.contract_id}`}>↻ renouvellement annuel {m.fee_stage.split("_")[1]}</span> : null}</div>
              <div className="col-span-2">
                <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${m.progress.total ? Math.round(m.progress.done / m.progress.total * 100) : 0}%` }} /></div>
                <div className="text-[10px] text-zinc-500 mt-0.5">{m.progress.done}/{m.progress.total}</div>
              </div>
              <div className="col-span-2 text-right">
                {m.complete ? <span className="text-[11px] px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400">✓ Dossier OK</span>
                  : <span className={`text-[11px] px-2 py-0.5 rounded ${PRIO[m.priority]}`}>{m.priority}</span>}
              </div>
            </button>
          ))}
        </div>
      )}

      {settingsOpen && <FeeSettingsModal onClose={() => { setSettingsOpen(false); load(); }} />}
      {detailId && <MemberDetailModal cid={detailId} onClose={() => { setDetailId(null); load(); }} />}
    </div>
  );
}

function FeeSettingsModal({ onClose }) {
  const [s, setS] = useState(null);
  useEffect(() => { (async () => { try { const { data } = await api.get("/equipment/settings"); setS(data.settings); } catch {} })(); }, []);
  const save = async () => {
    try { await api.put("/equipment/settings", s); toast.success("Frais enregistrés"); onClose(); }
    catch { toast.error("Erreur"); }
  };
  if (!s) return null;
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-zinc-950 border border-zinc-800 rounded-lg max-w-md w-full my-10" onClick={(e) => e.stopPropagation()} data-testid="fee-settings-modal">
        <div className="border-b border-zinc-800 px-6 py-4 flex items-center justify-between"><div className="font-bold text-zinc-100">⚙️ Frais d'inscription par discipline</div><button onClick={onClose} className="text-zinc-400 text-xl">×</button></div>
        <div className="p-6 space-y-4">
          {[["frais_muay_thai_kickboxing", "Kickboxing / Muay Thaï (au début)"], ["frais_jujutsu_japonais", "Jiu-jitsu Can-Ryu (au début)"], ["frais_bjj", "Jiu-jitsu brésilien (par ceinture bleu/mauve/brune/noire)"], ["frais_petits_samourais", "Petits Samouraïs (au début — 35 $ décidé le 2026-09-16)"], ["frais_powerkick_her", "PowerKick Her (au début — 35 $ décidé le 2026-09-16)"], ["frais_renouvellement", "Renouvellement annuel — toutes disciplines (25 $ décidé le 2026-09-17, dû à chaque anniversaire de la date de début)"]].map(([k, lbl]) => (
            <div key={k}>
              <label className="text-xs text-zinc-400 block mb-1">{lbl}</label>
              <div className="flex items-center gap-2"><span className="text-zinc-500">$</span>
                <input type="number" value={s[k] ?? 0} onChange={(e) => setS((p) => ({ ...p, [k]: e.target.value }))} data-testid={`fee-input-${k}`} className="flex-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100" /></div>
            </div>
          ))}
          <button onClick={save} data-testid="fee-save-btn" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white rounded py-2 font-semibold">Enregistrer</button>
        </div>
      </div>
    </div>
  );
}

function TierBlock({ title, items, storeItems, onCycle, accent }) {
  if (!items || items.length === 0) return null;
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  return (
    <div className="mb-4">
      <div className={`text-xs font-bold uppercase tracking-wider mb-2 ${accent}`}>{title}</div>
      <div className="space-y-1.5">
        {items.map((it) => {
          const key = `eq_${it.key}`;
          const st = storeItems[key]?.status || "a_faire";
          const meta = STATUS_META[st];
          const proof = storeItems[key]?.proof_url;
          return (
            <button key={it.key} onClick={() => onCycle(key, st)} data-testid={`eqitem-${it.key}`}
              className="w-full flex items-center justify-between gap-2 text-left text-sm px-3 py-2 rounded border border-zinc-800 bg-zinc-900/40 hover:bg-zinc-900">
              <span className="text-zinc-200">{it.label}{it.cle && <span className="ml-1 text-emerald-400" title="Obligatoire pour tous">★</span>}</span>
              <span className="flex items-center gap-2 shrink-0">
                {proof && (
                  <span role="link" tabIndex={0} data-testid={`eqproof-${it.key}`}
                    onClick={(e) => { e.stopPropagation(); window.open(`${apiBase}${proof}`, "_blank", "noopener"); }}
                    className="text-[10px] text-cyan-400 hover:text-cyan-300 underline">📷 Preuve</span>
                )}
                <span className={`text-[10px] px-2 py-0.5 rounded border ${meta.cls}`}>{meta.label}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MemberDetailModal({ cid, onClose }) {
  const [data, setData] = useState(null);
  const [fee, setFee] = useState(null);

  const load = useCallback(async () => {
    try {
      const [c, f] = await Promise.all([api.get(`/equipment/checklist/${cid}`), api.get(`/equipment/fee-status/${cid}`)]);
      setData(c.data); setFee(f.data);
    } catch { toast.error("Erreur"); }
  }, [cid]);
  const [checkOpen, setCheckOpen] = useState(false);
  useEffect(() => { load(); }, [load]);

  const cycle = async (key, cur) => {
    const next = NEXT[cur] || "a_faire";
    try { await api.post(`/equipment/checklist/${cid}/item`, { item_key: key, status: next }); load(); }
    catch { toast.error("Erreur"); }
  };
  const markPaid = async () => {
    try { await api.post(`/equipment/fee/${cid}/mark-paid`, { method: "manuel" }); toast.success("Frais marqués payés"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const complete = async (val) => {
    try { await api.post(`/equipment/checklist/${cid}/complete`, { complete: val }); toast.success(val ? "Dossier fermé ✓" : "Dossier rouvert"); load(); }
    catch { toast.error("Erreur"); }
  };

  if (!data) return null;
  const def = data.definition;
  const store = data.items || {};
  const pct = data.progress.total ? Math.round(data.progress.done / data.progress.total * 100) : 0;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-zinc-950 border border-zinc-800 rounded-lg max-w-lg w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="member-detail-modal">
        {/* Carte discipline + statut global */}
        <div className="border-b border-zinc-800 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-bold text-lg text-zinc-100">{data.contract.full_name}</div>
              <div className="text-xs text-zinc-500">{def.discipline_label} · {data.contract.belt || "—"}</div>
              <button onClick={() => setCheckOpen(true)} className="mt-1 text-xs px-2 py-1 rounded bg-amber-500/20 text-amber-300 hover:bg-amber-500/30" data-testid="verify-equipment-btn">🥋 Vérifier l'équipement de l'élève</button>
              {checkOpen && <StudentEquipmentCheck contractId={cid} onClose={() => setCheckOpen(false)} />}
            </div>
            <button onClick={onClose} className="text-zinc-400 text-xl">×</button>
          </div>
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs mb-1"><span className="text-zinc-400">Progression du premier mois</span><span className="text-emerald-400 font-bold" data-testid="detail-progress">{data.progress.done}/{data.progress.total}</span></div>
            <div className="h-2 rounded-full bg-zinc-800 overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} data-testid="detail-progress-bar" /></div>
          </div>
        </div>

        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {/* Frais d'inscription */}
          {fee && (
            <div className="mb-5 p-3 rounded border border-zinc-800 bg-zinc-900/40" data-testid="fee-block">
              <div className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">Frais d'inscription</div>
              {!fee.applicable && <div className="text-sm text-zinc-500">Non applicable pour cette discipline/ceinture.</div>}
              {fee.applicable && (
                <div className="flex items-center justify-between">
                  <div className="text-sm"><span className={FEE_META[fee.status]?.cls}>{FEE_META[fee.status]?.label}</span> · {fee.amount}$ <span className="text-zinc-600 text-xs">({fee.stage})</span></div>
                  {fee.status === "non_paye" && <button onClick={markPaid} data-testid="mark-fee-paid-btn" className="text-xs px-3 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-500">Marquer payé</button>}
                </div>
              )}
            </div>
          )}

          {/* Adaptations selon l'âge */}
          {data.age_notes && data.age_notes.length > 0 && (
            <div className="mb-5 p-3 rounded border border-amber-700/40 bg-amber-900/10" data-testid="age-notes-block">
              <div className="text-xs font-bold uppercase tracking-wider text-amber-400 mb-1">
                Recommandations selon l'âge{data.contract.age ? ` (${data.contract.age} ans)` : ""}
              </div>
              <ul className="text-sm text-zinc-300 space-y-1">
                {data.age_notes.map((n, i) => <li key={i} data-testid={`age-note-${i}`}>• {n.label}</li>)}
              </ul>
            </div>
          )}

          {/* Checklist de base */}
          <div className="mb-4">
            <div className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Obligations administratives</div>
            <div className="space-y-1.5">
              {data.base_checklist.map((b) => {
                const st = store[b.key]?.status || "a_faire";
                const meta = STATUS_META[st];
                return (
                  <button key={b.key} onClick={() => cycle(b.key, st)} data-testid={`baseitem-${b.key}`} className="w-full flex items-center justify-between gap-2 text-left text-sm px-3 py-2 rounded border border-zinc-800 bg-zinc-900/40 hover:bg-zinc-900">
                    <span className="text-zinc-200">{b.label}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded border shrink-0 ${meta.cls}`}>{meta.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* 3 blocs équipement : Obligatoire → Recommandé → Avancé */}
          <TierBlock title="Obligatoire" items={def.obligatoire} storeItems={store} onCycle={cycle} accent="text-red-400" />
          <TierBlock title="Recommandé" items={def.recommande} storeItems={store} onCycle={cycle} accent="text-sky-400" />
          <TierBlock title="Avancé" items={def.avance} storeItems={store} onCycle={cycle} accent="text-zinc-500" />

          <div className="text-[11px] text-zinc-500 mb-4">💡 Cliquez un item pour faire défiler son statut : À faire → En cours → Complété → Vérifié.</div>
        </div>

        {/* Validation finale */}
        <div className="border-t border-zinc-800 px-6 py-4 flex items-center justify-between">
          {data.complete
            ? <span className="text-sm text-emerald-400" data-testid="detail-verified-badge">✓ Dossier vérifié / fermé</span>
            : <span className="text-sm text-zinc-500">Dossier en cours</span>}
          <button onClick={() => complete(!data.complete)} data-testid="complete-toggle-btn"
            className={`text-sm px-4 py-2 rounded font-semibold ${data.complete ? "border border-zinc-700 text-zinc-300 hover:bg-zinc-800" : "bg-emerald-600 text-white hover:bg-emerald-500"}`}>
            {data.complete ? "Rouvrir le dossier" : "✓ Vérifié par coach/admin — fermer"}
          </button>
        </div>
      </div>
    </div>
  );
}
